import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle, Notebook as NotebookIcon } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";

const LEVELS = ["Remind", "Coaching/Cueing", "Supervise", "Stand By", "Hands On", "Complete"];

function Block({ title, children, testid }) {
  if (!children) return null;
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid={testid}>
      <p className="text-xs font-semibold uppercase tracking-wide text-stone-400">{title}</p>
      <div className="mt-2 text-sm leading-relaxed text-stone-700">{children}</div>
    </div>
  );
}

export default function StaffCarePlanPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const [plan, setPlan] = useState(null);
  const [notebook, setNotebook] = useState(null);
  const [signed, setSigned] = useState(false);
  const [busy, setBusy] = useState(false);
  const [signTask, setSignTask] = useState(null);
  const [adopted, setAdopted] = useState(null);
  const [typedName, setTypedName] = useState("");
  const [consent, setConsent] = useState(false);
  const [recordBusy, setRecordBusy] = useState(false);
  const today = new Date().toISOString().slice(0, 10);

  const load = useCallback(() => {
    api.get(`/clients/${clientId}/careplan-view`)
      .then((r) => setPlan(r.data))
      .catch((e) => { toast.error(formatApiError(e)); navigate("/dashboard"); });
    api.get(`/clients/${clientId}/notebook`).then((r) => setNotebook(r.data)).catch(() => setNotebook(null));
    api.get("/my/careplans").then((r) => {
      const me2 = (r.data.clients || []).find((c) => c.client_id === clientId);
      setSigned(!!me2?.signed_today);
    }).catch(() => {});
    api.get("/my/tasks").then((r) => {
      const t = (r.data.tasks || []).find((x) => (x.key || "") === `sign-plan:${clientId}`);
      setSignTask(t || null);
      if (t) api.get("/my/signature").then((r2) => setAdopted(r2.data)).catch(() => {});
    }).catch(() => {});
  }, [clientId, navigate]);
  useEffect(() => { load(); }, [load]);

  const sign = async () => {
    setBusy(true);
    try {
      await api.post(`/clients/${clientId}/careplan-ack`, { shift_date: today });
      setSigned(true);
      toast.success("Signed off for today's shift");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const signRecord = async (useAdopted) => {
    if (!consent) { toast.error("Please check the signature consent box first"); return; }
    if (!useAdopted && !typedName.trim()) { toast.error("Type your full name to sign"); return; }
    setRecordBusy(true);
    try {
      await api.post(`/employees/${me.id}/care-plan-signatures`,
        { client_id: clientId, use_adopted: useAdopted, typed_name: typedName, consent: true });
      toast.success("Care plan signed — the signed copy with your certificate is on file");
      setSignTask(null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRecordBusy(false); }
  };

  if (!plan) return <div className="mx-auto max-w-3xl px-4 py-10 text-sm text-stone-400">Loading…</div>;
  const cp = plan.careplan || {};
  const tasks = (cp.tasks || []).filter((t) => t.selected);

  return (
    <div className="mx-auto max-w-3xl px-4 py-8" data-testid="staff-careplan-page">
      <button onClick={() => navigate("/dashboard")} className="mb-4 flex items-center gap-1 text-xs font-medium text-stone-500 hover:text-primary" data-testid="careplan-back">
        <ArrowLeft size={14} /> Back to dashboard
      </button>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-stone-900">Care plan — {plan.client_name}</h1>
          {plan.updated_at && <p className="mt-1 text-xs text-stone-400">Plan effective/updated {String(plan.updated_at).slice(0, 10)}</p>}
        </div>
        <Button onClick={sign} disabled={busy || signed} className={`rounded-full ${signed ? "bg-[#2F6B4F] text-white" : "bg-primary text-primary-foreground"}`} data-testid="careplan-signoff">
          <CheckCircle size={16} className="mr-1.5" /> {signed ? "Signed for today's shift" : "I've reviewed the care plan — sign off"}
        </Button>
      </div>
      <p className="mt-2 text-xs text-stone-500">
        Review this plan at the start of your shift and sign off. Your wrap-up each shift re-confirms it.
      </p>
      {signTask && (
        <div className="mt-3 rounded-lg border border-primary/30 bg-primary/[0.04] p-4" data-testid="careplan-sign-record">
          <p className="text-sm font-semibold text-stone-900">Signature of record — the office asked you to sign this care plan</p>
          <p className="mt-1 text-xs text-stone-500">
            Read the plan below, then sign. Your signature, the date/time, and this exact version of the plan are
            kept together as proof — your name stays attached to what you actually read.
          </p>
          {adopted?.adopted && (
            <div className="mt-2 rounded-lg border border-stone-200 bg-white p-3" data-testid="careplan-adopted-block">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-stone-400">Your adopted signature</p>
              {adopted.pretty_png && <img src={adopted.pretty_png} alt={`Adopted signature of ${adopted.typed_name}`} className="mt-1 h-12" data-testid="careplan-adopted-preview" />}
            </div>
          )}
          {!adopted?.adopted && (
            <Input value={typedName} onChange={(e) => setTypedName(e.target.value)} placeholder="Type your full legal name to sign"
              className="mt-2 border-stone-300 bg-white" data-testid="careplan-sign-typed" />
          )}
          <label className="mt-2 flex items-start gap-2.5 rounded-lg border border-stone-200 bg-white px-3 py-2.5" data-testid="careplan-sign-consent-row">
            <input type="checkbox" className="mt-0.5" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="careplan-sign-consent" />
            <span className="text-xs text-stone-600">I have reviewed this document and intend to sign it electronically.</span>
          </label>
          <Button onClick={() => signRecord(!!adopted?.adopted)} disabled={recordBusy || !consent || (!adopted?.adopted && !typedName.trim())}
            className="mt-2 rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="careplan-sign-record-submit">
            {recordBusy ? "Signing…" : "Sign Document"}
          </Button>
        </div>
      )}
      {plan.needs_ack && plan.latest_update && (
        <div className="mt-3 rounded-lg border border-[#C0392B]/30 bg-[#C0392B]/[0.05] p-4" data-testid="careplan-update-banner">
          <p className="text-sm font-semibold text-[#C0392B]">This care plan was updated ({plan.latest_update.version}) — review and sign off below.</p>
          <p className="mt-1 text-xs text-stone-600">What changed: {(plan.latest_update.summary || []).join("; ")}</p>
        </div>
      )}

      <div className="mt-5 space-y-4">
        <Block title="The need" testid="cp-need">{cp.need || null}</Block>
        <Block title="The goal" testid="cp-goal">{cp.goal || null}</Block>
        {tasks.length > 0 && (
          <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="cp-tasks">
            <p className="text-xs font-semibold uppercase tracking-wide text-stone-400">Your tasks — at exactly the level listed</p>
            <div className="mt-2 space-y-3">
              {tasks.map((t) => (
                <div key={t.code} className="border-b border-stone-50 pb-2 last:border-0" data-testid={`cp-task-${t.code}`}>
                  <p className="text-sm font-medium text-stone-800">
                    {t.code} — {t.label}
                    {(t.levels || []).length > 0 && (
                      <span className="ml-2">
                        {t.levels.map((l) => (
                          <span key={l} className={`mr-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${LEVELS.indexOf(l) >= 4 ? "bg-primary/10 text-primary" : "bg-stone-100 text-stone-500"}`}>{l}</span>
                        ))}
                      </span>
                    )}
                  </p>
                  {t.frequency && <p className="text-xs text-stone-500">Frequency: {t.frequency}</p>}
                  {t.instruction && <p className="mt-0.5 text-xs italic text-stone-600">{t.instruction}</p>}
                </div>
              ))}
            </div>
          </div>
        )}
        <Block title="Routines & preferences" testid="cp-routines">{cp.routines_preferences || null}</Block>
        <Block title="Safety instructions" testid="cp-safety">{cp.safety_instructions || null}</Block>
        <Block title="Off limits" testid="cp-offlimits">{cp.off_limits || null}</Block>
        <Block title="Medication reminders" testid="cp-meds">
          {(cp.med_box_location || cp.reminder_instructions) ? (
            <>{cp.med_box_location && <p>Medication box: {cp.med_box_location}</p>}{cp.reminder_instructions && <p className="mt-1">{cp.reminder_instructions}</p>}</>
          ) : null}
        </Block>
        <Block title="Transfers & equipment" testid="cp-transfers">
          {(cp.transfer_instructions || cp.equipment_used) ? (
            <>{cp.transfer_instructions && <p>{cp.transfer_instructions}</p>}{cp.equipment_used && <p className="mt-1">Equipment: {cp.equipment_used}</p>}</>
          ) : null}
        </Block>
        <Block title="Transportation" testid="cp-transport">{cp.transport_instructions || null}</Block>
      </div>

      {notebook && (
        <div className="mt-6 rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="notebook-section">
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-stone-400">
            <NotebookIcon size={14} /> The notebook — what other caregivers saw and did
          </p>
          {notebook.notes.length === 0 ? (
            <p className="mt-3 text-sm text-stone-400">No wrap-ups yet.</p>
          ) : (
            <div className="mt-3 space-y-3">
              {notebook.notes.map((n) => (
                <div key={n.id || n.shift_date + n.caregiver} className={`rounded-lg border p-3 ${n.mine ? "border-primary/30 bg-primary/[0.03]" : "border-stone-100 bg-accent/30"}`} data-testid={`notebook-entry-${n.id}`}>
                  <p className="text-xs font-semibold text-stone-600">{n.shift_date} · {n.caregiver}{n.mine ? " (you)" : ""}</p>
                  {n.summary && <p className="mt-1 text-sm text-stone-700">{n.summary}</p>}
                  {!n.summary && n.free_note && <p className="mt-1 text-sm text-stone-700">{n.free_note}</p>}
                  {(n.tasks || []).length > 0 && (
                    <p className="mt-1 text-xs text-stone-500">
                      {n.tasks.map((t) => `${t.label}: ${t.status === "declined" ? `declined (${t.decline_reason || "?"})` : t.status}`).join(" · ")}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
